import { observer } from "mobx-react";
import moment from "moment";
import "rc-tree/assets/index.css";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import {
  canCancelReservation,
  formatNumberWithCommas,
  getOptionNameFromServiceName,
  isCurrentDateTimeGreater,
} from "../../../constants/constants";
import bookingStatusEnum from "../../../constants/enums/booking-status-enum";
import { CancellationTypeEnum } from "../../../constants/enums/cancellation-type-enum";
import { serviceTypeEnum } from "../../../constants/enums/service-type-enum";
import { useStore } from "../../../contexts/store-provider";
import { formatTimeDisplay } from "../../../helpers/date-time-helper";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import CustomerTopNavbar from "../../../shared-components/navbar/customer-top-navbar";
import ConfirmPopup from "../../../shared-components/popup/confirm/confirm-popup";
import ConfirmPopupHOC from "../../../shared-components/popup/confirm/confirm-popup-hoc";
import { formatMessage } from "../../../translations/format-message";
import "./reservation-list.css";
const ReservationListDetails: React.FC = (props: any) => {
  const location = useLocation();
  const history = useHistory();
  const bookingDetail = location.state ? (location.state as any) : null;
  let totalCharges = 0;

  const {
    bookingManagementStore,
    masterManagementStore,
    reservationSchedulerStore,
    storeCmsStore,
    headquarterStore
  } = useStore();
  const {
    UpdateAppointmentStatusService,
    addUpdateAppointmentState,
    resetAppointmentState,
  } = bookingManagementStore;
  const { GetStoreCmsByIdService, resetGetStoreCmsState, cmsStoreState } =
    storeCmsStore;
  const {
    getHeadquarterCancelationPolicyForReservationDetails,
    GetHeadquarterByIdService,
    resetGetHeadquarterCancelationDetail,
    GetHeadquarterCancelationBookingStoreDetail,
    addUpdateHeadquarterCancelationPolicyState,
  } = headquarterStore;
  
  const {
    UpdateReservationCancelStatusService,
    updateReservationCancelState,
    resetUpdateReservationCancelState,
  } = reservationSchedulerStore;

  const { getReservationSettingDetail, GetReservationSettingByIdService } =
    masterManagementStore;

  useEffect(() => {
    GetReservationSettingByIdService(bookingDetail?.StoreId);
    GetStoreCmsByIdService(bookingDetail?.StoreId);
    if (bookingDetail?.Store?.HeadquarterId)
      GetHeadquarterCancelationBookingStoreDetail(
        bookingDetail?.StoreId,
        bookingDetail?.Store?.HeadquarterId
      );
    return () => {
      resetGetStoreCmsState();
      resetGetHeadquarterCancelationDetail();
    };
  }, []);

  useEffect(() => {
    if (updateReservationCancelState.success) {
      toast.success(formatMessage("cancelled_success"));
      resetUpdateReservationCancelState();
      history.goBack();
    }
  }, [updateReservationCancelState.success]);

  const formatTime = (minutes: number) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0 && mins > 0)
      return `${hrs} ${formatMessage("hour")} ${mins} ${formatMessage(
        "minutes"
      )}`;
    if (hrs > 0) return `${hrs} ${formatMessage("hour")}`;
    return `${mins} ${formatMessage("minutes")}`;
  };

  const handelRedirectToStoreDetail = (storeId: number) => {
    history.push(`/Store/${storeId}`);
  };

  const handleCancellationSubmit = () => {
    UpdateReservationCancelStatusService({
      Id: bookingDetail?.Id, // Appointment ID
      CancellationType: CancellationTypeEnum.Web, // Cancellation type
    });
  }

  return (
    <React.Fragment>
      <FormLoader
        loading={
          updateReservationCancelState.inProgress ||
          addUpdateHeadquarterCancelationPolicyState.inProgress ||
          cmsStoreState.inProgress
        }
      />
      {props.showConfirmPopup && (
        <ConfirmPopup
          isLoading={addUpdateHeadquarterCancelationPolicyState.inProgress}
          title={formatMessage("confirm_canellation_title")}
          message={formatMessage("confirm_canellation_title_info")}
          modalSubmit={handleCancellationSubmit}
          modalClosed={props.confirmPopupToggleHandler}
        />
      )}

      <CustomerTopNavbar />

      <ol className="breadcrumb ps-2 pt-3">
        <li className="breadcrumb-item">
          <svg
            className="me-1"
            fill="currentColor"
            version="1.1"
            id="fi_25694"
            x="0px"
            y="0px"
            width="15"
            height="15"
            viewBox="0 0 460.298 460.297"
          >
            <g>
              <g>
                <path
                  d="M230.149,120.939L65.986,256.274c0,0.191-0.048,0.472-0.144,0.855c-0.094,0.38-0.144,0.656-0.144,0.852v137.041
                        c0,4.948,1.809,9.236,5.426,12.847c3.616,3.613,7.898,5.431,12.847,5.431h109.63V303.664h73.097v109.64h109.629
                        c4.948,0,9.236-1.814,12.847-5.435c3.617-3.607,5.432-7.898,5.432-12.847V257.981c0-0.76-0.104-1.334-0.288-1.707L230.149,120.939
                        z"
                ></path>
                <path
                  d="M457.122,225.438L394.6,173.476V56.989c0-2.663-0.856-4.853-2.574-6.567c-1.704-1.712-3.894-2.568-6.563-2.568h-54.816
                        c-2.666,0-4.855,0.856-6.57,2.568c-1.711,1.714-2.566,3.905-2.566,6.567v55.673l-69.662-58.245
                        c-6.084-4.949-13.318-7.423-21.694-7.423c-8.375,0-15.608,2.474-21.698,7.423L3.172,225.438c-1.903,1.52-2.946,3.566-3.14,6.136
                        c-0.193,2.568,0.472,4.811,1.997,6.713l17.701,21.128c1.525,1.712,3.521,2.759,5.996,3.142c2.285,0.192,4.57-0.476,6.855-1.998
                        L230.149,95.817l197.57,164.741c1.526,1.328,3.521,1.991,5.996,1.991h0.858c2.471-0.376,4.463-1.43,5.996-3.138l17.703-21.125
                        c1.522-1.906,2.189-4.145,1.991-6.716C460.068,229.007,459.021,226.961,457.122,225.438z"
                ></path>
              </g>
            </g>
          </svg>
          <FormattedMessage id="Home" />
        </li>
        <li className="breadcrumb-item" aria-current="page">
          <FormattedMessage id="reservation" />
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          <FormattedMessage id="reservation_details" />
        </li>
      </ol>
      <div className="container-fluid mt-4 p-0 reservation_detail_wrapper">
        <h2>{formatMessage("booking_information")}</h2>
        <div className="row g-0 border-top border-end">
          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("status")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            <span
              className={`d-inline-block py-2 px-4 text-white ${bookingDetail?.AppointmentStatus == 0
                  ? "bg-success"
                  : bookingDetail?.AppointmentStatus == 1
                    ? "bg-danger"
                    : "completed-customer-reservation"
                }`}
            >
              <FormattedMessage
                id={`status_${bookingStatusEnum[bookingDetail?.AppointmentStatus]
                  }`}
              />
            </span>
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("date_and_time")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            {moment(bookingDetail?.Date)?.format("YYYY-MM-DD")} (
              {formatMessage(
                moment(bookingDetail.Date)?.format("ddd"))}
            ){" "}
            {moment(bookingDetail?.Time, "HH:mm:ss").format("HH:mm")}
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("store_details")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            <h4>{bookingDetail?.Store?.StoreName}</h4>
             {/* {
              storeCmsStore.store &&
              <div className="text-muted text-wrap" dangerouslySetInnerHTML={{ __html: storeCmsStore.store.StoreDetails }}>
              </div>
            } */}
            <h5>
              {formatMessage("contact_number")}:{" "}
              {bookingDetail?.Store?.MobileNumber}
            </h5>
            <p className="small text-muted text-wrap">{bookingDetail?.Store?.Address}</p>
            <div className="row">
              <div className="col-6">
                <a
                  href={bookingDetail?.Store?.Url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    id="fi_3394645"
                    height="16"
                    viewBox="0 0 32 32"
                    width="16"
                    fill="currentColor"
                    data-name="Layer 3"
                  >
                    <path d="m24 4.051a12 12 0 1 0 -15.782 18.074 29.186 29.186 0 0 1 6.9 8.351 1 1 0 0 0 .882.524 1 1 0 0 0 .88-.526l.082-.154a29.5 29.5 0 0 1 6.89-8.257 11.989 11.989 0 0 0 .148-18.012zm-8 13.949a5 5 0 1 1 5-5 5.006 5.006 0 0 1 -5 5z"></path>
                  </svg>
                  {bookingDetail?.Store?.Url
                    ? formatMessage("map")
                    : formatMessage("no_location_available")}
                </a>
              </div>
              <div className="col-6 text-end">
                <button
                  onClick={() => {
                    handelRedirectToStoreDetail(bookingDetail?.Store?.StoreCode);
                  }}
                  type="button"
                  className="btn btn-link p-0 ms-1"
                >
                  <FormattedMessage id="see_details" />
                </button>
              </div>
            </div>
          </div>
          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("total_time")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            <h4>
              {bookingDetail?.TotalTime != null
                ? formatTime(bookingDetail.TotalTime)
                : "-"}
            </h4>
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("menu")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            {bookingDetail?.ServiceRequestedVMss?.map(
              (service: any, serviceIndex: number) => {
                totalCharges += Number(service?.Charges);
                let serviceName =
                  service.ServiceType === serviceTypeEnum.Option
                    ? getOptionNameFromServiceName(service?.ServiceName)
                    : service?.ServiceName;
                return (
                  <div key={serviceIndex} className="row border">
                    <div className="col-6 p-2 text-wrap">
                      {serviceName}
                      {
                        service.ServiceType === serviceTypeEnum.Option &&
                        <span className="badge bg-primary rounded ms-2">
                          <FormattedMessage id="option" />
                        </span>
                      }
                    </div>
                    <div className="col-3 p-2 border-start text-center">
                      <i className="bi bi-currency-yen me-1"></i>
                      {formatNumberWithCommas(service?.Charges)}
                    </div>
                    <div className="col-3 p-2 border-start text-center">
                      {formatTimeDisplay(Number(service?.TimeValue ?? 0))}
                    </div>
                  </div>
                );
              }
            )}
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("staff")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            {
              bookingDetail?.StaffPreference == 1 ? formatMessage("male_hope") : bookingDetail.StaffPreference == 2 ? 
              formatMessage("women_hope") : bookingDetail?.StaffPreference == 0 ? 
              formatMessage("no_nomination") : 
              <>
                {bookingDetail?.StaffName
                  ? bookingDetail?.StaffName
                  : formatMessage("specific")}
              </>
            }
            {" "}
            - <i className="bi bi-currency-yen me-1"></i>
            {formatNumberWithCommas(bookingDetail?.NominationFee)}
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("amount")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            <h4>
              <i className="bi bi-currency-yen me-1"></i>
              {formatNumberWithCommas(bookingDetail?.TotalAmount)}
            </h4>
            <p className="text-muted small">(<FormattedMessage id="tax_label" />: <i className="bi bi-currency-yen me-1"></i>{formatNumberWithCommas(bookingDetail?.TotalTaxes + (bookingDetail?.ConsumptionTax ?? 0))})</p>
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("payment_method")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            {formatMessage("onsite_payment")}
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("cancellation_deadline")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            {getHeadquarterCancelationPolicyForReservationDetails.HeadquarterCancelationText &&
              getHeadquarterCancelationPolicyForReservationDetails.HeadquarterCancelationText?.replace(
                "{time}",
                String(getReservationSettingDetail?.CancelationTime)
              )}
          </div>          

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("cancellation_policy")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3 text-wrap">
            <div
              dangerouslySetInnerHTML={{
                __html:
                  getHeadquarterCancelationPolicyForReservationDetails.HeadquarterCancelationPolicy,
              }}
            ></div>
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("precaution")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
           <div
              dangerouslySetInnerHTML={{
                __html:
                  getHeadquarterCancelationPolicyForReservationDetails.Precaution,
              }}
            ></div>
          </div>

          <div className="bg-light col-md-4 col-sm-5 col-12 border-start border-bottom p-3">
            <h4>{formatMessage("reservation_id")}</h4>
          </div>
          <div className=" col-md-8 col-sm-7 col-12 border-start border-bottom p-3">
            {bookingDetail?.Id}
          </div>
        </div>

        <div className="d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-dark px-2 px-md-4 mt-3"
            onClick={() => history.goBack()}
          >
            {formatMessage("return")}
          </button>

          {bookingDetail?.AppointmentStatus !== bookingStatusEnum.Cancelled && (
              <button
                type="button"
                className="btn btn-outline-danger px-2 px-md-4 mt-3 ms-4"
                onClick={ () => {
                    const isPast = isCurrentDateTimeGreater(
                      moment(bookingDetail?.Date).format("YYYY-MM-DD"),
                      bookingDetail?.Time
                    );
                    if (isPast) {
                      toast.error(formatMessage("cancellation_not_allowed")); // or whatever message key
                    } else if (
                      canCancelReservation(
                        bookingDetail?.Date,
                        bookingDetail?.Time,
                        Number(getReservationSettingDetail?.CancelationTime)
                      ) == false
                    ) {
                      toast.error(formatMessage("you_can_cancel_reservation_prior_to_hour"));
                    } else {
                      props.confirmPopupToggleHandler();
                    }
                  }
                }
              >
                {formatMessage("cancel_booking")}
              </button>
            )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default ConfirmPopupHOC(observer(ReservationListDetails));
